/**
 * CheckboxCard — an approval or an acceptance as a card: the checkbox at its
 * start, the words beside it, the whole card the target. Ticked, it wears the
 * scope's edge and tint (the hero's, inside a ToneScope). A keyboard focus
 * rings the card.
 *
 * Presentation only: the words are the caller's, translated.
 */
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Checkbox } from '../primitives/checkbox';
import { Label } from '../primitives/label';

export interface CheckboxCardProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function CheckboxCard({
  id,
  checked,
  onCheckedChange,
  children,
  disabled,
  className,
  'data-testid': testId,
}: CheckboxCardProps) {
  return (
    <Label
      htmlFor={id}
      data-slot="checkbox-card"
      data-checked={checked}
      data-testid={testId}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 text-sm leading-snug font-normal transition-colors',
        'has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-2',
        checked ? 'border-primary bg-accent' : 'hover:border-primary/40',
        disabled && 'cursor-not-allowed',
        className
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        className="mt-0.5"
      />
      <span className="min-w-0">{children}</span>
    </Label>
  );
}
